import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusinessType } from '@/contexts/BusinessTypeContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Trash2, Eye, EyeOff, Package, DollarSign, 
  Tag, Image, Clock, UtensilsCrossed, Plus, X, AlertCircle, CheckCircle
} from 'lucide-react';
import { formatCurrency, formatDateTime } from '@/lib/utils-helpers';
import PhotoUpload from '@/components/vendor/PhotoUpload';

export default function VendorItemDetail() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { config } = useBusinessType();
  
  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'General',
    category_node_id: null,
    category_path: '',
    image_url: '',
    is_available: true,
    stock_quantity: 0,
    sku: '',
    prep_time: 15,
    variants: [],
    modifiers: [],
    dietary_tags: []
  });

  useEffect(() => {
    fetchItem();
    fetchCategories();
  }, [itemId]);

  const fetchItem = async () => {
    try {
      const res = await api.get(`/vendor/menu/${itemId}`);
      const itemData = res.data;
      setItem(itemData);
      setFormData({
        name: itemData.name || '',
        description: itemData.description || '',
        price: String(itemData.price || ''),
        category: itemData.category || 'General',
        category_node_id: itemData.category_node_id || null,
        category_path: itemData.category_path || '',
        image_url: itemData.image_url || '',
        is_available: itemData.is_available !== undefined ? itemData.is_available : true,
        stock_quantity: itemData.stock_quantity || 0,
        sku: itemData.sku || '',
        prep_time: itemData.prep_time || 15,
        variants: itemData.variants || [],
        modifiers: itemData.modifiers || [],
        dietary_tags: itemData.dietary_tags || []
      });
    } catch (err) {
      toast.error('Failed to load item details');
      navigate('/vendor/menu');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/vendor/inventory/category-tree');
      setCategories(res.data.tree || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/vendor/menu/${itemId}`, {
        ...formData,
        price: Number(formData.price),
        stock_quantity: Number(formData.stock_quantity),
        prep_time: Number(formData.prep_time)
      });
      toast.success('Item updated successfully');
      fetchItem(); // Reload to show updated data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${item.name}"? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/vendor/menu/${itemId}`);
      toast.success('Item deleted');
      navigate('/vendor/menu');
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const toggleAvailability = async () => {
    try {
      await api.put(`/vendor/menu/${itemId}/toggle`);
      setFormData(prev => ({ ...prev, is_available: !prev.is_available }));
      toast.success(`Item ${formData.is_available ? 'hidden' : 'made available'}`);
    } catch (err) {
      toast.error('Failed to update availability');
    }
  };

  // Build category options from tree
  const buildCategoryOptions = (nodes, prefix = '') => {
    let options = [];
    for (const node of nodes) {
      const label = prefix ? `${prefix} > ${node.name}` : node.name;
      options.push({ value: node.id, label, path: node.path, name: node.name });
      if (node.children?.length > 0) {
        options = options.concat(buildCategoryOptions(node.children, label));
      }
    }
    return options;
  };

  const categoryOptions = [
    { value: 'none', label: 'General (No Category)', path: '', name: 'General' },
    ...buildCategoryOptions(categories)
  ];

  const handleCategoryChange = (value) => {
    if (value === 'none') {
      setFormData({ ...formData, category: 'General', category_node_id: null, category_path: '' });
    } else {
      const selected = categoryOptions.find(opt => opt.value === value);
      if (selected) {
        setFormData({ 
          ...formData, 
          category: selected.name,
          category_node_id: selected.value,
          category_path: selected.path
        });
      }
    }
  };

  // Variants management
  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [...formData.variants, { name: '', options: '', price_modifier: 0 }]
    });
  };

  const updateVariant = (index, field, value) => {
    const updated = [...formData.variants];
    updated[index][field] = value;
    setFormData({ ...formData, variants: updated });
  };

  const removeVariant = (index) => {
    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index)
    });
  };

  // Modifiers management
  const addModifier = () => {
    setFormData({
      ...formData,
      modifiers: [...formData.modifiers, { name: '', price: 0, is_default: false }]
    });
  };

  const updateModifier = (index, field, value) => {
    const updated = [...formData.modifiers];
    updated[index][field] = value;
    setFormData({ ...formData, modifiers: updated });
  };

  const removeModifier = (index) => {
    setFormData({
      ...formData,
      modifiers: formData.modifiers.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vendor/menu')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="item-detail-title">{item?.name || `${config.itemLabel} Details`}</h1>
            <p className="text-sm text-muted-foreground">
              Created {formatDateTime(item?.created_at)} • Updated {formatDateTime(item?.updated_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={toggleAvailability}
            data-testid="toggle-availability-btn"
          >
            {formData.is_available ? (
              <><Eye className="h-4 w-4 mr-2" /> Visible</>
            ) : (
              <><EyeOff className="h-4 w-4 mr-2" /> Hidden</>
            )}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            data-testid="delete-item-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-item-btn"
          >
            {saving ? (
              'Saving...'
            ) : (
              <><Save className="h-4 w-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      </div>

      {/* Availability Alert */}
      {!formData.is_available && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This item is currently <strong>hidden</strong> from customers. Toggle visibility to make it available.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
          {config.fields.hasVariants && <TabsTrigger value="variants" data-testid="tab-variants">Variants & Modifiers</TabsTrigger>}
          {config.fields.hasStock && <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>}
          <TabsTrigger value="advanced" data-testid="tab-advanced">Advanced</TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="name">{config.itemLabel} Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Margherita Pizza"
                    data-testid="item-name-input"
                  />
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your item..."
                    rows={4}
                    data-testid="item-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price ($) *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="pl-9"
                      data-testid="item-price-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category_node_id || 'none'}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger id="category" data-testid="item-category-select">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.category_path && (
                    <p className="text-xs text-muted-foreground">Path: {formData.category_path}</p>
                  )}
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="image_url">Image URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="image_url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      data-testid="item-image-input"
                    />
                    {formData.image_url && (
                      <Button variant="outline" size="icon" onClick={() => window.open(formData.image_url, '_blank')}>
                        <Image className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {formData.image_url && (
                    <div className="mt-2">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-32 h-32 object-cover rounded-lg border"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>
                <div className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Product Photos
                </div>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Upload up to 5 high-quality photos. First photo will be the primary image in listings.
              </p>
            </CardHeader>
            <CardContent>
              <PhotoUpload
                itemId={itemId}
                currentPhotos={item?.photos || []}
                onUploadSuccess={() => fetchItem()}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Variants & Modifiers Tab */}
        <TabsContent value="variants" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Variants</CardTitle>
                <Button size="sm" onClick={addVariant} data-testid="add-variant-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Variant
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.variants.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No variants added. Variants allow customers to choose options like size or color.
                </p>
              ) : (
                formData.variants.map((variant, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Variant name (e.g., Size)"
                        value={variant.name}
                        onChange={(e) => updateVariant(index, 'name', e.target.value)}
                      />
                      <Input
                        placeholder="Options (comma-separated, e.g., Small, Medium, Large)"
                        value={variant.options}
                        onChange={(e) => updateVariant(index, 'options', e.target.value)}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price modifier ($)"
                        value={variant.price_modifier}
                        onChange={(e) => updateVariant(index, 'price_modifier', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeVariant(index)}
                      className="text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Modifiers / Add-ons</CardTitle>
                <Button size="sm" onClick={addModifier} data-testid="add-modifier-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Modifier
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.modifiers.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No modifiers added. Modifiers are optional add-ons like extra cheese or toppings.
                </p>
              ) : (
                formData.modifiers.map((modifier, index) => (
                  <div key={index} className="flex gap-2 items-center p-3 border rounded-lg">
                    <Input
                      placeholder="Modifier name (e.g., Extra Cheese)"
                      value={modifier.name}
                      onChange={(e) => updateModifier(index, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={modifier.price}
                      onChange={(e) => updateModifier(index, 'price', e.target.value)}
                      className="w-24"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeModifier(index)}
                      className="text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Stock Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU / Product Code</Label>
                  <Input
                    id="sku"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="PROD-001"
                    data-testid="item-sku-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stock">Stock Quantity</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    placeholder="0"
                    data-testid="item-stock-input"
                  />
                </div>
              </div>

              {formData.stock_quantity < 5 && formData.stock_quantity > 0 && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    Low stock alert: Only {formData.stock_quantity} units remaining.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Advanced Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="prep_time">Preparation Time (minutes)</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="prep_time"
                    type="number"
                    value={formData.prep_time}
                    onChange={(e) => setFormData({ ...formData, prep_time: e.target.value })}
                    className="pl-9"
                    data-testid="item-prep-time-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Estimated time to prepare this item. Affects delivery estimates.
                </p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Dietary Tags (comma-separated)</Label>
                <Input
                  value={formData.dietary_tags.join(', ')}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    dietary_tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                  })}
                  placeholder="vegan, gluten-free, halal"
                  data-testid="item-dietary-tags-input"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.dietary_tags.map((tag, index) => (
                    <Badge key={index} variant="outline">
                      <Tag className="h-3 w-3 mr-1" /> {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
